import cn from 'classnames';
import { forwardRef } from 'react';

import Button from '../button/button';

import type { ForwardedRef, ReactElement } from 'react';

import type { ToggleButtonProps } from './toggle-button.types';

import './toggle-button.scss';

/**
 * The current component is used as an argument for the `forwardRef` function to forward ref.
 * However, to keep the generic interface of the `ToggleButton` component we have to cast type
 * after wrapping this component with the `forwardRef` function.
 */
function ToggleButtonInner<T extends number | string = string>(
  props: ToggleButtonProps<T>,
  forwardedRef: ForwardedRef<HTMLButtonElement>,
): ReactElement {
  const {
    children,
    className,
    color = 'default',
    disabled,
    onClick,
    selected,
    type = 'secondary',
    value,
    ...restProps
  } = props;

  const classes = cn(
    'rbui-toggle-button',
    {
      'rbui-toggle-button--disabled': disabled,
      'rbui-toggle-button--selected': selected,
    },
    className,
  );

  return (
    <Button
      {...restProps}
      aria-pressed={selected}
      className={classes}
      color={color}
      disabled={disabled}
      onClick={(event) => onClick?.(event, value)}
      ref={forwardedRef}
      type={type}
    >
      {children}
    </Button>
  );
}

const ToggleButton = forwardRef(ToggleButtonInner);

ToggleButton.displayName = 'ToggleButton';

/**
 * The `ToggleButton` component can be used to group related options.
 *
 * ## Usage
 *
 * ### Basic
 * ```tsx
 *  import { ToggleButton, ToggleButtonGroup } from '@bloomreach/react-banana-ui`;
 *
 *  export default BasicToggleGroup() {
 *    const [value, setValue] = useState('mobile');
 *
 *    return (
 *      <ToggleButtonGroup
 *        value={value}
 *        onChange={(event, value) => { setValue(value); }}
 *      >
 *        <ToggleButton value="web">Web</ToggleButton>
 *        <ToggleButton value="mobile">Mobile</ToggleButton>
 *        <ToggleButton value="desktop">Desktop</ToggleButton>
 *      </ToggleButtonGroup>
 *    );
 *  };
 * ```
 *
 * ### Defined value type
 * The `ToggleButton` is generic component and determinate value type
 * based on `value` or it might be explicitly defined like
 *
 * ```tsx
 * type MyValue = 'one' | 'two' | 'three';
 * <ToggleButton<MyValue> value="two">...</ToggleButton>
 * ```
 * by default `value` has a `string` type.
 */
export default ToggleButton as <T extends number | string = string>(
  props: ToggleButtonProps<T> & { ref?: ForwardedRef<HTMLButtonElement> },
) => ReturnType<typeof ToggleButtonInner>; // Check the comment for `ToggleButtonInner` to get a reason.
